import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { registerUser, verifyEmail, resendVerification } from '../api/api';
import StyledSelect from '../components/StyledSelect';

/* ─── tiny helpers ─── */
const EyeIcon = ({ show }) =>
  show ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

const Spinner = () => (
  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

const BlobBg = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{background:'#f5cd79'}} />
    <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{background:'#cf6a87'}} />
  </div>
);

/* Returns webmail URL for known providers, falls back to mailto */
const getEmailLink = (email) => {
  const domain = email.split('@')[1]?.toLowerCase() || '';
  if (domain === 'gmail.com' || domain === 'googlemail.com')
    return { url: 'https://mail.google.com', label: 'Open Gmail' };
  if (domain === 'yahoo.com' || domain === 'yahoo.co.in' || domain === 'ymail.com')
    return { url: 'https://mail.yahoo.com', label: 'Open Yahoo Mail' };
  if (['outlook.com','hotmail.com','live.com','msn.com','hotmail.co.uk'].includes(domain))
    return { url: 'https://outlook.live.com', label: 'Open Outlook' };
  if (domain === 'icloud.com' || domain === 'me.com' || domain === 'mac.com')
    return { url: 'https://www.icloud.com/mail', label: 'Open iCloud Mail' };
  if (domain === 'protonmail.com' || domain === 'proton.me')
    return { url: 'https://mail.proton.me', label: 'Open ProtonMail' };
  return { url: `https://mail.${domain}`, label: 'Open Email' };
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const teamSizeOptions = [
    { value: '', label: 'Select size' },
    { value: '1-10', label: '1-10' },
    { value: '11-50', label: '11-50' },
    { value: '51-200', label: '51-200' },
    { value: '201-1000', label: '201-1000' },
    { value: '1000+', label: '1000+' },
  ];
  const currencyOptions = [
    { value: '', label: 'Select currency' },
    { value: 'PKR', label: 'PKR - Pakistani Rupee' },
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'AED', label: 'AED - UAE Dirham' },
    { value: 'SAR', label: 'SAR - Saudi Riyal' },
    { value: 'QAR', label: 'QAR - Qatari Riyal' },
    { value: 'KWD', label: 'KWD - Kuwaiti Dinar' },
    { value: 'BHD', label: 'BHD - Bahraini Dinar' },
    { value: 'OMR', label: 'OMR - Omani Rial' },
    { value: 'JOD', label: 'JOD - Jordanian Dinar' },
    { value: 'EGP', label: 'EGP - Egyptian Pound' },
    { value: 'TRY', label: 'TRY - Turkish Lira' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'KRW', label: 'KRW - South Korean Won' },
    { value: 'SGD', label: 'SGD - Singapore Dollar' },
    { value: 'MYR', label: 'MYR - Malaysian Ringgit' },
    { value: 'IDR', label: 'IDR - Indonesian Rupiah' },
    { value: 'THB', label: 'THB - Thai Baht' },
    { value: 'VND', label: 'VND - Vietnamese Dong' },
    { value: 'BDT', label: 'BDT - Bangladeshi Taka' },
    { value: 'LKR', label: 'LKR - Sri Lankan Rupee' },
    { value: 'NPR', label: 'NPR - Nepalese Rupee' },
    { value: 'AFN', label: 'AFN - Afghan Afghani' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'SEK', label: 'SEK - Swedish Krona' },
    { value: 'NOK', label: 'NOK - Norwegian Krone' },
    { value: 'DKK', label: 'DKK - Danish Krone' },
    { value: 'PLN', label: 'PLN - Polish Zloty' },
    { value: 'CZK', label: 'CZK - Czech Koruna' },
    { value: 'HUF', label: 'HUF - Hungarian Forint' },
    { value: 'RUB', label: 'RUB - Russian Ruble' },
    { value: 'BRL', label: 'BRL - Brazilian Real' },
    { value: 'MXN', label: 'MXN - Mexican Peso' },
    { value: 'ARS', label: 'ARS - Argentine Peso' },
    { value: 'CLP', label: 'CLP - Chilean Peso' },
    { value: 'COP', label: 'COP - Colombian Peso' },
    { value: 'ZAR', label: 'ZAR - South African Rand' },
    { value: 'NGN', label: 'NGN - Nigerian Naira' },
    { value: 'KES', label: 'KES - Kenyan Shilling' },
    { value: 'GHS', label: 'GHS - Ghanaian Cedi' },
    { value: 'MAD', label: 'MAD - Moroccan Dirham' },
    { value: 'TND', label: 'TND - Tunisian Dinar' },
    { value: 'DZD', label: 'DZD - Algerian Dinar' },
    { value: 'IQD', label: 'IQD - Iraqi Dinar' },
    { value: 'IRR', label: 'IRR - Iranian Rial' },
    { value: 'ILS', label: 'ILS - Israeli Shekel' },
    { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
    { value: 'TWD', label: 'TWD - Taiwan Dollar' },
    { value: 'NZD', label: 'NZD - New Zealand Dollar' },
  ];

  const countryOptions = [
    { value: '', label: 'Select country' },
    { value: 'Pakistan', label: 'Pakistan' },
    { value: 'United Arab Emirates', label: 'United Arab Emirates' },
    { value: 'Saudi Arabia', label: 'Saudi Arabia' },
    { value: 'Qatar', label: 'Qatar' },
    { value: 'Kuwait', label: 'Kuwait' },
    { value: 'Bahrain', label: 'Bahrain' },
    { value: 'Oman', label: 'Oman' },
    { value: 'Afghanistan', label: 'Afghanistan' },
    { value: 'Bangladesh', label: 'Bangladesh' },
    { value: 'Sri Lanka', label: 'Sri Lanka' },
    { value: 'Nepal', label: 'Nepal' },
    { value: 'United States', label: 'United States' },
    { value: 'United Kingdom', label: 'United Kingdom' },
    { value: 'Canada', label: 'Canada' },
    { value: 'Australia', label: 'Australia' },
    { value: 'Germany', label: 'Germany' },
    { value: 'France', label: 'France' },
    { value: 'Italy', label: 'Italy' },
    { value: 'Spain', label: 'Spain' },
    { value: 'Netherlands', label: 'Netherlands' },
    { value: 'Belgium', label: 'Belgium' },
    { value: 'Switzerland', label: 'Switzerland' },
    { value: 'Sweden', label: 'Sweden' },
    { value: 'Norway', label: 'Norway' },
    { value: 'Denmark', label: 'Denmark' },
    { value: 'Finland', label: 'Finland' },
    { value: 'Poland', label: 'Poland' },
    { value: 'Russia', label: 'Russia' },
    { value: 'Turkey', label: 'Turkey' },
    { value: 'Egypt', label: 'Egypt' },
    { value: 'Jordan', label: 'Jordan' },
    { value: 'Iraq', label: 'Iraq' },
    { value: 'Iran', label: 'Iran' },
    { value: 'Lebanon', label: 'Lebanon' },
    { value: 'China', label: 'China' },
    { value: 'Japan', label: 'Japan' },
    { value: 'South Korea', label: 'South Korea' },
    { value: 'Singapore', label: 'Singapore' },
    { value: 'Malaysia', label: 'Malaysia' },
    { value: 'Indonesia', label: 'Indonesia' },
    { value: 'Thailand', label: 'Thailand' },
    { value: 'Vietnam', label: 'Vietnam' },
    { value: 'Philippines', label: 'Philippines' },
    { value: 'Hong Kong', label: 'Hong Kong' },
    { value: 'Taiwan', label: 'Taiwan' },
    { value: 'New Zealand', label: 'New Zealand' },
    { value: 'South Africa', label: 'South Africa' },
    { value: 'Nigeria', label: 'Nigeria' },
    { value: 'Kenya', label: 'Kenya' },
    { value: 'Ghana', label: 'Ghana' },
    { value: 'Morocco', label: 'Morocco' },
    { value: 'Tunisia', label: 'Tunisia' },
    { value: 'Algeria', label: 'Algeria' },
    { value: 'Brazil', label: 'Brazil' },
    { value: 'Mexico', label: 'Mexico' },
    { value: 'Argentina', label: 'Argentina' },
    { value: 'Colombia', label: 'Colombia' },
    { value: 'Chile', label: 'Chile' },
  ];

  const timezoneOptions = [
    { value: '', label: 'Select timezone' },
    { value: 'Asia/Karachi', label: 'Asia/Karachi (PKT, UTC+5)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GST, UTC+4)' },
    { value: 'Asia/Riyadh', label: 'Asia/Riyadh (AST, UTC+3)' },
    { value: 'Asia/Qatar', label: 'Asia/Qatar (AST, UTC+3)' },
    { value: 'Asia/Kuwait', label: 'Asia/Kuwait (AST, UTC+3)' },
    { value: 'Asia/Bahrain', label: 'Asia/Bahrain (AST, UTC+3)' },
    { value: 'Asia/Muscat', label: 'Asia/Muscat (GST, UTC+4)' },
    { value: 'Asia/Kabul', label: 'Asia/Kabul (AFT, UTC+4:30)' },
    { value: 'Asia/Dhaka', label: 'Asia/Dhaka (BST, UTC+6)' },
    { value: 'Asia/Colombo', label: 'Asia/Colombo (IST, UTC+5:30)' },
    { value: 'Asia/Kathmandu', label: 'Asia/Kathmandu (NPT, UTC+5:45)' },
    { value: 'Asia/Baghdad', label: 'Asia/Baghdad (AST, UTC+3)' },
    { value: 'Asia/Beirut', label: 'Asia/Beirut (EET, UTC+2)' },
    { value: 'Asia/Jerusalem', label: 'Asia/Jerusalem (IST, UTC+2)' },
    { value: 'Asia/Tehran', label: 'Asia/Tehran (IRST, UTC+3:30)' },
    { value: 'Asia/Amman', label: 'Asia/Amman (EET, UTC+2)' },
    { value: 'Asia/Cairo', label: 'Africa/Cairo (EET, UTC+2)' },
    { value: 'Africa/Casablanca', label: 'Africa/Casablanca (WET, UTC+1)' },
    { value: 'Africa/Lagos', label: 'Africa/Lagos (WAT, UTC+1)' },
    { value: 'Africa/Nairobi', label: 'Africa/Nairobi (EAT, UTC+3)' },
    { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST, UTC+2)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST, UTC+8)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST, UTC+9)' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul (KST, UTC+9)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT, UTC+8)' },
    { value: 'Asia/Kuala_Lumpur', label: 'Asia/Kuala_Lumpur (MYT, UTC+8)' },
    { value: 'Asia/Jakarta', label: 'Asia/Jakarta (WIB, UTC+7)' },
    { value: 'Asia/Bangkok', label: 'Asia/Bangkok (ICT, UTC+7)' },
    { value: 'Asia/Manila', label: 'Asia/Manila (PHT, UTC+8)' },
    { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT, UTC+8)' },
    { value: 'Asia/Taipei', label: 'Asia/Taipei (CST, UTC+8)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT, UTC+11)' },
    { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEDT, UTC+11)' },
    { value: 'Australia/Perth', label: 'Australia/Perth (AWST, UTC+8)' },
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZDT, UTC+13)' },
    { value: 'America/New_York', label: 'America/New_York (EST, UTC-5)' },
    { value: 'America/Chicago', label: 'America/Chicago (CST, UTC-6)' },
    { value: 'America/Denver', label: 'America/Denver (MST, UTC-7)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST, UTC-8)' },
    { value: 'America/Toronto', label: 'America/Toronto (EST, UTC-5)' },
    { value: 'America/Vancouver', label: 'America/Vancouver (PST, UTC-8)' },
    { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT, UTC-3)' },
    { value: 'America/Mexico_City', label: 'America/Mexico_City (CST, UTC-6)' },
    { value: 'America/Buenos_Aires', label: 'America/Buenos_Aires (ART, UTC-3)' },
    { value: 'Europe/London', label: 'Europe/London (GMT, UTC+0)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (CET, UTC+1)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET, UTC+1)' },
    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET, UTC+1)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (CET, UTC+1)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (CET, UTC+1)' },
    { value: 'Europe/Stockholm', label: 'Europe/Stockholm (CET, UTC+1)' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK, UTC+3)' },
    { value: 'Europe/Istanbul', label: 'Europe/Istanbul (TRT, UTC+3)' },
    { value: 'UTC', label: 'UTC (Universal Coordinated Time)' },
  ];

  /* ── Registration form state ── */
  const [form, setForm]         = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone: '',
    registration_type: 'create_org',
    org_name: '',
    org_industry: '',
    org_size: '',
    org_country: '',
    org_currency: 'PKR',
    org_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    invite_code: '',
  });
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  /* ── After register: OTP screen ── */
  const [sent, setSent]           = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  /* ── OTP input ── */
  const [otp, setOtp]           = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  /* ── After verify: success screen ── */
  const [verified, setVerified] = useState(false);

  /* ── Password visibility ── */
  const [showPass, setShowPass]            = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  /* ── Resend ── */
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  /* ────────────── Handlers ────────────── */
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim() || form.first_name.trim().length < 2) return setError('First name must be at least 2 characters');
    if (!form.last_name.trim() || form.last_name.trim().length < 2) return setError('Last name must be at least 2 characters');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (form.password !== form.confirm_password) return setError('Passwords do not match');
    if (form.registration_type === 'create_org' && !form.org_name.trim()) return setError('Organization name is required');
    if (form.registration_type === 'join_org' && !form.invite_code.trim()) return setError('Organization invite code is required');
    setLoading(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name:  form.last_name.trim(),
        email:      form.email.trim(),
        password:   form.password,
        phone:      form.phone.trim() || undefined,
        registration_type: form.registration_type,
      };
      if (form.registration_type === 'create_org') {
        payload.org_name = form.org_name.trim();
        payload.org_industry = form.org_industry.trim() || undefined;
        payload.org_size = form.org_size || undefined;
        payload.org_country = form.org_country.trim() || undefined;
        payload.org_currency = form.org_currency || undefined;
        payload.org_timezone = form.org_timezone || undefined;
      } else {
        payload.invite_code = form.invite_code.trim();
      }
      const res  = await registerUser(payload);
      const data = res.data.data;
      setSentEmail(data.email);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setOtpError('');
    if (value && index < 5) otpRefs[index + 1].current?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0)
      otpRefs[index - 1].current?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const next   = [...otp];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || '';
    setOtp(next);
    if (pasted.length > 0) otpRefs[Math.min(pasted.length, 5)].current?.focus();
  };

  const handleVerify = async () => {
    const code = otp.join('');
    if (code.length < 6) return setOtpError('Please enter all 6 digits');
    setVerifying(true);
    setOtpError('');
    try {
      await verifyEmail({ email: sentEmail, otp: code });
      setVerified(true);
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpRefs[0].current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setResendMsg('');
    setOtp(['', '', '', '', '', '']);
    setOtpError('');
    try {
      const res  = await resendVerification({ email: sentEmail });
      const data = res.data.data;
      setResendMsg('New OTP sent! Check your email.');
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) {
      setResendMsg(err.response?.data?.message || 'Could not resend. Try again.');
    } finally {
      setResending(false);
    }
  };

  /* ── Verified success screen ── */
  if (verified) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{background:'linear-gradient(135deg,#fef9ec 0%,#fff 50%,#fdf0f4 100%)'}}>
        <BlobBg />
        <div className="relative w-full max-w-sm text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-5 shadow-lg" style={{background:'linear-gradient(135deg,#22c55e,#16a34a)'}}>
            <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 mb-2">Email Verified!</h1>
          <p className="text-slate-500 text-sm mb-8">Your account is now active. You can log in.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-10 py-3 rounded-xl text-white font-semibold text-sm shadow-md transition"
            style={{background:'#c44569'}}
            onMouseEnter={e => e.currentTarget.style.background = '#cf6a87'}
            onMouseLeave={e => e.currentTarget.style.background = '#c44569'}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  /* ── OTP entry screen ── */
  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{background:'linear-gradient(135deg,#fef9ec 0%,#fff 50%,#fdf0f4 100%)'}}>
        <BlobBg />
        <div className="relative w-full max-w-md">
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-2 shadow-lg" style={{background:'linear-gradient(135deg,#c44569,#cf6a87)'}}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-xl font-extrabold text-slate-900 mb-0.5">Verify your email</h1>
            <p className="text-slate-500 text-xs">We sent a 6-digit code to</p>
            <p className="text-sm font-bold mt-0.5" style={{color:'#c44569'}}>{sentEmail}</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl border p-6" style={{borderColor:'#f9b8ca'}}>
            <p className="text-sm font-semibold text-slate-700 mb-4 text-center">Enter verification code</p>

            {/* 6-box OTP input */}
            <div className="flex justify-center gap-2 mb-1" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={otpRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  className="w-10 text-center text-xl font-extrabold rounded-xl border outline-none transition"
                  style={{
                    height:'44px',
                    borderColor: otpError ? '#f87171' : digit ? '#c44569' : '#f9b8ca',
                    background:  digit ? '#fdf0f4' : '#fafafa',
                    color: '#1e293b',
                    boxShadow: digit ? '0 0 0 2px rgba(196,69,105,0.12)' : 'none',
                  }}
                  onFocus={e => { e.target.style.borderColor='#c44569'; e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.18)'; }}
                  onBlur={e  => { e.target.style.borderColor = otpError ? '#f87171' : digit ? '#c44569' : '#f9b8ca'; e.target.style.boxShadow = digit ? '0 0 0 2px rgba(196,69,105,0.12)' : 'none'; }}
                />
              ))}
            </div>

            {otpError && (
              <p className="text-center text-xs mt-1.5 font-semibold" style={{color:'#c44569'}}>{otpError}</p>
            )}

            {/* Open email button */}
            {(() => {
              const { url, label } = getEmailLink(sentEmail);
              return (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full mt-3 py-2 rounded-xl text-xs font-semibold border transition"
                  style={{background:'#fdf0f4', color:'#c44569', borderColor:'#f9b8ca'}}
                  onMouseEnter={e => { e.currentTarget.style.background='#fce0e8'; e.currentTarget.style.borderColor='#c44569'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='#fdf0f4'; e.currentTarget.style.borderColor='#f9b8ca'; }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {label}
                  <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              );
            })()}

            <button
              onClick={handleVerify}
              disabled={verifying || otp.join('').length < 6}
              className="w-full mt-3 py-2.5 rounded-xl text-white font-semibold text-sm transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              style={{background:'#c44569'}}
              onMouseEnter={e => { if (!verifying) e.currentTarget.style.background = '#cf6a87'; }}
              onMouseLeave={e => e.currentTarget.style.background = '#c44569'}
            >
              {verifying ? <span className="flex items-center justify-center gap-2"><Spinner /> Verifying...</span> : 'Verify OTP'}
            </button>

            <div className="mt-3 text-center">
              {resendMsg && (
                <p className="text-xs mb-1 font-medium" style={{color: resendMsg.includes('sent') ? '#b08800' : '#c44569'}}>{resendMsg}</p>
              )}
              <p className="text-xs text-slate-400">Didn't receive it?{' '}
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="font-semibold hover:underline disabled:opacity-60 transition"
                  style={{color:'#c44569'}}
                >
                  {resending ? 'Sending...' : 'Resend OTP'}
                </button>
              </p>
            </div>
          </div>

          <p className="text-sm text-slate-500 mt-5 text-center">
            <Link to="/login" className="font-semibold hover:underline" style={{color:'#c44569'}}>← Back to Login</Link>
          </p>
        </div>
      </div>
    );
  }

  /* ── Registration form ── */
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{background:'linear-gradient(135deg,#fef9ec 0%,#fff 50%,#fdf0f4 100%)'}}>
      <BlobBg />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl shadow-lg mb-2" style={{background:'#c44569'}}>
            <Wallet size={22} color="#fff" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900">EMS</h1>
          <p className="text-slate-500 text-xs mt-0.5">Expense Management App</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border p-6" style={{borderColor:'#f9b8ca'}}>
          <h2 className="text-lg font-bold text-slate-800 mb-0.5">Create your account</h2>
          <p className="text-slate-500 text-xs mb-4">Start managing your expenses for free</p>

          {error && (
            <div className="mb-4 flex items-center gap-2 text-sm px-4 py-3 rounded-xl" style={{background:'#fce0e8',border:'1px solid #f9b8ca',color:'#c44569'}}>
              <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* First Name + Last Name row */}
            <div className="grid grid-cols-2 gap-3">
              {[
                {label:'First Name', name:'first_name', placeholder:'John'},
                {label:'Last Name',  name:'last_name',  placeholder:'Doe'},
              ].map(f=>(
                <div key={f.name}>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">{f.label}</label>
                  <input
                    className="w-full px-3 py-2 rounded-xl border text-slate-900 text-sm outline-none transition"
                    style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                    onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                    onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                    type="text" name={f.name} value={form[f.name]}
                    onChange={handleChange} placeholder={f.placeholder}
                    required autoFocus={f.name==='first_name'}
                  />
                </div>
              ))}
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address</label>
              <input
                className="w-full px-3 py-2 rounded-xl border text-slate-900 text-sm outline-none transition"
                style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                type="email" name="email" value={form.email}
                onChange={handleChange} placeholder="you@example.com" required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Password</label>
              <div className="relative">
                <input
                  className="w-full px-3 py-2 pr-10 rounded-xl border text-slate-900 text-sm outline-none transition"
                  style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                  onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                  onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                  type={showPass ? 'text' : 'password'} name="password" value={form.password}
                  onChange={handleChange} placeholder="Minimum 6 characters" required
                />
                <button type="button" tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={()=>setShowPass(s=>!s)}>
                  <EyeIcon show={showPass} />
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm Password</label>
              <div className="relative">
                <input
                  className="w-full px-3 py-2 pr-10 rounded-xl border text-slate-900 text-sm outline-none transition"
                  style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                  onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                  onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                  type={showConfirmPass ? 'text' : 'password'} name="confirm_password" value={form.confirm_password}
                  onChange={handleChange} placeholder="Re-enter password" required
                />
                <button type="button" tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={()=>setShowConfirmPass(s=>!s)}>
                  <EyeIcon show={showConfirmPass} />
                </button>
              </div>
            </div>

            {/* Phone (optional) */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Phone Number <span className="font-normal text-slate-400">(optional)</span>
              </label>
              <input
                className="w-full px-3 py-2 rounded-xl border text-slate-900 text-sm outline-none transition"
                style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                type="tel" name="phone" value={form.phone}
                onChange={handleChange} placeholder="+92 300 1234567"
              />
            </div>

            <div className="pt-1">
              <label className="block text-xs font-semibold text-slate-700 mb-1">Organization Setup</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, registration_type: 'create_org' }))}
                  className="px-3 py-2 rounded-xl border text-xs font-semibold transition"
                  style={{
                    background: form.registration_type === 'create_org' ? '#fce0e8' : '#fff',
                    borderColor: form.registration_type === 'create_org' ? '#c44569' : '#f9b8ca',
                    color: '#7a1f3d',
                  }}
                >
                  Create New Organization
                </button>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, registration_type: 'join_org' }))}
                  className="px-3 py-2 rounded-xl border text-xs font-semibold transition"
                  style={{
                    background: form.registration_type === 'join_org' ? '#fce0e8' : '#fff',
                    borderColor: form.registration_type === 'join_org' ? '#c44569' : '#f9b8ca',
                    color: '#7a1f3d',
                  }}
                >
                  Join Existing Organization
                </button>
              </div>
            </div>

            {form.registration_type === 'create_org' ? (
              <>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Organization Name</label>
                  <input
                    className="w-full px-3 py-2 rounded-xl border text-slate-900 text-sm outline-none transition"
                    style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                    onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                    onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                    type="text" name="org_name" value={form.org_name}
                    onChange={handleChange} placeholder="Acme Technologies Pvt Ltd" required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Industry</label>
                    <input
                      className="w-full px-3 py-2 rounded-xl border text-slate-900 text-sm outline-none transition"
                      style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                      onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                      onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                      type="text" name="org_industry" value={form.org_industry}
                      onChange={handleChange} placeholder="FinTech"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Team Size</label>
                    <StyledSelect
                      value={form.org_size}
                      onChange={(next) => setForm((prev) => ({ ...prev, org_size: next }))}
                      options={teamSizeOptions}
                      placeholder="Select size"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Country</label>
                    <StyledSelect
                      value={form.org_country}
                      onChange={(next) => setForm((prev) => ({ ...prev, org_country: next }))}
                      options={countryOptions}
                      placeholder="Pakistan"
                      searchable
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Currency</label>
                    <StyledSelect
                      value={form.org_currency}
                      onChange={(next) => setForm((prev) => ({ ...prev, org_currency: next }))}
                      options={currencyOptions}
                      placeholder="Select currency"
                      searchable
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Timezone</label>
                  <StyledSelect
                    value={form.org_timezone}
                    onChange={(next) => setForm((prev) => ({ ...prev, org_timezone: next }))}
                    options={timezoneOptions}
                    placeholder="Asia/Karachi"
                    searchable
                  />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Organization Invite Code</label>
                <input
                  className="w-full px-3 py-2 rounded-xl border text-slate-900 text-sm outline-none transition uppercase"
                  style={{borderColor:'#f9b8ca',background:'#fdf0f4'}}
                  onFocus={e=>{e.target.style.borderColor='#c44569';e.target.style.boxShadow='0 0 0 3px rgba(196,69,105,0.15)';e.target.style.background='#fff'}}
                  onBlur={e=>{e.target.style.borderColor='#f9b8ca';e.target.style.boxShadow='none';e.target.style.background='#fdf0f4'}}
                  type="text" name="invite_code" value={form.invite_code}
                  onChange={handleChange} placeholder="EMS-ORG-123456" required
                />
              </div>
            )}
            <button
              className="w-full py-2.5 text-white font-semibold rounded-xl text-sm transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed mt-1"
              style={{background:'#c44569'}}
              onMouseEnter={e=>{if(!loading)e.currentTarget.style.background='#cf6a87'}}
              onMouseLeave={e=>{e.currentTarget.style.background='#c44569'}}
              type="submit" disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner /> Creating account...
                </span>
              ) : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-4">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold hover:underline" style={{color:'#c44569'}}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;

